import type { StagedWell } from 'types/well-workspace';

export interface CountyGroup {
  county: string;
  wells: StagedWell[];
}

export interface StateGroup {
  state: string;
  counties: CountyGroup[];
  /** Wells under this state, across all its counties — the count the row shows. */
  count: number;
}

/** What a group row with no state or county on the well is labelled. */
export const UNGROUPED = '—';

/**
 * The fields the search matches on. Kept structural so the same filter serves the
 * workspace's staged wells and the full inventory rows the Add-or-remove modal lists,
 * which carry a different shape.
 */
export interface SearchableWell {
  name: string | null;
  uwi: string;
  stateAbbreviation?: string | null;
  county?: string | null;
}

/**
 * Narrows wells to those matching a free-text query.
 *
 * The mock's placeholder promises "wells, state, county", so the group labels are
 * searchable too and typing a county name does not look broken.
 * @param {T[]} wells The wells to narrow.
 * @param {string} query Raw text from the search box.
 * @return {T[]} The wells to show; every well when the query is blank.
 */
export function filterWells<T extends SearchableWell>(
  wells: T[],
  query: string
): T[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return wells;

  return wells.filter((well) =>
    [well.name, well.uwi, well.stateAbbreviation, well.county].some((field) =>
      field?.toLowerCase().includes(needle)
    )
  );
}

/**
 * Groups wells into the tree's State -> County -> Well shape, each level sorted by
 * label so the tree order is stable between renders and between workspaces.
 * @param {StagedWell[]} wells The wells to group (already filtered).
 * @return {StateGroup[]} One entry per state, counties and wells nested and sorted.
 */
export function groupWells(wells: StagedWell[]): StateGroup[] {
  const byState = new Map<string, Map<string, StagedWell[]>>();

  wells.forEach((well) => {
    const state = well.stateAbbreviation || UNGROUPED;
    const county = well.county || UNGROUPED;
    const counties = byState.get(state) ?? new Map<string, StagedWell[]>();
    const bucket = counties.get(county) ?? [];

    bucket.push(well);
    counties.set(county, bucket);
    byState.set(state, counties);
  });

  return Array.from(byState.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([state, counties]) => ({
      state,
      count: Array.from(counties.values()).reduce(
        (total, bucket) => total + bucket.length,
        0
      ),
      counties: Array.from(counties.entries())
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([county, bucket]) => ({
          county,
          // Sorted copy — mutating the array would reorder the caller's `wells`, which
          // the stepper reads for prev/next.
          // A well the backend could not name sorts by its UWI, where the tree shows
          // the UWI in the name's place too; `name` is nullable and this runs in a
          // top-level memo, so a bare localeCompare took the whole page down.
          wells: [...bucket].sort((left, right) =>
            (left.name ?? left.uwi).localeCompare(right.name ?? right.uwi)
          ),
        })),
    }));
}

/**
 * The wells flattened in the order the tree draws them.
 *
 * The stepper walks this rather than the backend's array: the two disagree, because the
 * tree sorts by state, county then name, so Next used to jump to a row far from the
 * highlight and could even move upwards.
 * @param {StagedWell[]} wells The workspace's wells.
 * @return {StagedWell[]} The same wells, in tree order.
 */
export function treeOrder(wells: StagedWell[]): StagedWell[] {
  return groupWells(wells).flatMap((state) =>
    state.counties.flatMap((county) => county.wells)
  );
}
